import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import {
  Home,
  ChevronRight,
  Landmark,
  Ship,
  Factory,
  Building2,
  ArrowUpRight,
  CheckCircle2,
  MapPin,
  Layers3,
  Network,
} from "lucide-react";

import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const heroBg = "/assets/images/bg2.jpeg";
const sectionBg = "/assets/images/bg2.jpeg";

const years = [
  {
    year: "1982",
    title: "Private Investment Invitation",
    text:
      "The Nigerian Ports Authority invited private investors to participate in port infrastructure development.",
    icon: Building2,
  },
  {
    year: "1995",
    title: "Infrastructure Growth",
    text:
      "Intels had developed warehouses, quay aprons, berths and strategic logistics facilities at Onne.",
    icon: Factory,
  },
  {
    year: "1996",
    title: "Legal Foundation",
    text:
      "The Federal Government declared Onne an Oil and Gas Free Zone through Decree No. 8 of 1996.",
    icon: Landmark,
  },
  {
    year: "2000",
    title: "Regulatory Transition",
    text:
      "OGFZA took over regulatory functions for the Oil and Gas Free Zones.",
    icon: Network,
  },
  {
    year: "2001",
    title: "Operations Commenced",
    text:
      "The Authority began operations in Onne, Rivers State.",
    icon: Ship,
  },
];

const foundationPoints = [
  "The Oil and Gas Export Free Zone Act of 1996 empowered the Federal Government to designate and regulate free zones.",
  "The first Oil and Gas Export Free Zone was established at Onne in Rivers State.",
  "The Onne Free Zone grew through a public-private partnership model involving public infrastructure and private-sector investment.",
  "Nicotes, later known as Intels, and Brawal acquired leases and invested heavily in infrastructure.",
  "By 1995, Intels had developed warehouses, quay aprons, berths and major logistics infrastructure.",
  "OGFZA took over regulatory functions in 2000 and commenced operations in Onne in 2001.",
];

const OGFZAHistory = () => {
  const pageRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from(".hero-badge", {
        opacity: 0,
        y: 30,
        duration: 0.8,
        ease: "power3.out",
      });

      gsap.from(".hero-title", {
        opacity: 0,
        y: 80,
        duration: 1.1,
        ease: "power4.out",
      });

      gsap.from(".hero-text", {
        opacity: 0,
        y: 40,
        delay: 0.2,
        duration: 0.9,
        ease: "power3.out",
      });

      gsap.utils.toArray(".reveal-up").forEach((el) => {
        gsap.from(el, {
          opacity: 0,
          y: 65,
          duration: 0.85,
          ease: "power3.out",
          scrollTrigger: {
            trigger: el,
            start: "top 86%",
          },
        });
      });

      gsap.utils.toArray(".timeline-item").forEach((el, index) => {
        gsap.from(el, {
          opacity: 0,
          x: index % 2 === 0 ? -55 : 55,
          duration: 0.85,
          ease: "power3.out",
          scrollTrigger: {
            trigger: el,
            start: "top 88%",
          },
        });
      });

      gsap.to(".floating-orb-1", {
        y: 45,
        x: -25,
        duration: 7,
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut",
      });

      gsap.to(".floating-orb-2", {
        y: -45,
        x: 25,
        duration: 8,
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut",
      });
    }, pageRef);

    return () => ctx.revert();
  }, []);

  return (
    <main ref={pageRef} className="bg-white overflow-hidden">
      {/* HERO */}
      <section className="relative min-h-screen flex items-center overflow-hidden text-white">
        <img
          src={heroBg}
          alt="OGFZA History"
          className="absolute inset-0 w-full h-full object-cover"
        />

        <div className="absolute inset-0 bg-[#041018]/85" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#082567]/95 via-[#026B07]/70 to-[#F2AF11]/30" />

        <div className="floating-orb-1 absolute top-0 left-0 w-[520px] h-[520px] rounded-full bg-[#082567]/45 blur-3xl" />
        <div className="floating-orb-2 absolute bottom-0 right-0 w-[520px] h-[520px] rounded-full bg-[#F2AF11]/20 blur-3xl" />

        <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.05)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.05)_1px,transparent_1px)] bg-[size:80px_80px]" />

        <div className="relative max-w-7xl mx-auto px-6 py-32">
          <div className="hero-badge inline-flex items-center gap-2 px-5 py-2 rounded-full bg-white/10 border border-white/15 backdrop-blur-md mb-8">
            <span className="w-2 h-2 rounded-full bg-[#F2AF11]" />
            Institutional History
          </div>

          <h1
            className="hero-title text-6xl md:text-8xl font-black leading-[0.95] max-w-5xl"
            style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
          >
            OGFZA History
          </h1>

          <p className="hero-text mt-8 max-w-3xl text-white/85 text-lg leading-[1.9]">
            The story of OGFZA began with Nigeria’s strategic decision to create
            a dedicated oil and gas free zone framework backed by public
            regulation, private investment and world-class port logistics.
          </p>

          <div className="hero-text flex items-center gap-2 mt-8 text-sm text-white/75">
            <Link
              to="/"
              className="flex items-center gap-1 hover:text-[#F2AF11] transition-colors"
            >
              <Home size={14} />
              Home
            </Link>

            <ChevronRight size={14} />

            <Link
              to="/about"
              className="hover:text-[#F2AF11] transition-colors"
            >
              About Us
            </Link>

            <ChevronRight size={14} />

            <span className="text-[#F2AF11]">OGFZA History</span>
          </div>
        </div>
      </section>

      {/* INTRO */}
      <section className="relative py-28 bg-white">
        <div className="absolute top-0 right-0 w-[420px] h-[420px] rounded-full bg-[#F2AF11]/10 blur-3xl" />
        <div className="absolute bottom-0 left-0 w-[380px] h-[380px] rounded-full bg-[#026B07]/10 blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-[0.85fr_1.15fr] gap-14 items-start">
            <div className="reveal-up">
              <p className="text-[#026B07] font-black tracking-[0.2em] uppercase text-xs mb-4">
                Historical Background
              </p>

              <h2
                className="text-4xl md:text-6xl font-black text-[#082567] leading-tight mb-6"
                style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
              >
                From Port Infrastructure to Free Zone Authority
              </h2>

              <div className="w-24 h-1.5 rounded-full bg-[#F2AF11] mb-7" />

              <p className="text-slate-700 leading-[2] text-[16.5px]">
                OGFZA’s history is closely linked to the development of Onne as
                a strategic oil and gas logistics base and to Nigeria’s decision
                to formalize a dedicated free zone regulatory structure.
              </p>
            </div>

            <div className="reveal-up bg-white rounded-[34px] p-7 md:p-10 border border-slate-200 shadow-[0_24px_80px_-50px_rgba(8,37,103,0.45)]">
              <div className="flex flex-col md:flex-row gap-8">
                <div className="md:w-[90px]">
                  <div className="w-16 h-16 rounded-2xl bg-[#F2AF11]/15 text-[#082567] flex items-center justify-center border border-[#F2AF11]/25">
                    <Landmark size={30} />
                  </div>
                </div>

                <div className="flex-1">
                  <h3
                    className="text-3xl font-black text-[#082567] mb-4"
                    style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
                  >
                    The Legal Beginning
                  </h3>

                  <p className="text-slate-700 leading-[2]">
                    The Oil and Gas Export Free Zone Act of 1996 empowered the
                    Federal Government to designate and regulate free zones.
                    This created the legal basis for a specialized oil and gas
                    free zone system in Nigeria.
                  </p>

                  <div className="mt-8 pt-8 border-t border-slate-200">
                    <p className="text-slate-700 leading-[2]">
                      The first zone at Onne grew from public-private
                      partnerships. In 1982, the Nigerian Ports Authority
                      invited private investors. Nicotes, later known as Intels,
                      and Brawal acquired leases and invested heavily in
                      infrastructure.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TIMELINE */}
      <section className="relative py-28 bg-[#FAFBFC] overflow-hidden">
        <div className="absolute inset-0 opacity-[0.04] bg-[linear-gradient(to_right,#082567_1px,transparent_1px),linear-gradient(to_bottom,#082567_1px,transparent_1px)] bg-[size:70px_70px]" />

        <div className="relative max-w-6xl mx-auto px-6">
          <div className="reveal-up max-w-3xl mx-auto text-center mb-20">
            <p className="text-[#026B07] font-black tracking-[0.2em] uppercase text-xs mb-4">
              Development Path
            </p>

            <h2
              className="text-4xl md:text-6xl font-black text-[#082567] leading-tight"
              style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
            >
              Key Years That Shaped OGFZA
            </h2>

            <p className="mt-6 text-slate-600 leading-[1.9]">
              A clean historical timeline showing the major institutional and
              infrastructure milestones behind the Authority.
            </p>
          </div>

          <div className="relative max-w-4xl mx-auto">
            <div className="absolute left-5 md:left-1/2 top-0 bottom-0 w-px bg-slate-200 md:-translate-x-1/2" />

            {years.map((item, index) => {
              const Icon = item.icon;
              const isLeft = index % 2 === 0;

              return (
                <div
                  key={index}
                  className={`timeline-item relative mb-12 md:mb-16 flex ${
                    isLeft ? "md:justify-start" : "md:justify-end"
                  }`}
                >
                  <div className="absolute left-5 md:left-1/2 top-7 w-5 h-5 rounded-full bg-white border-[5px] border-[#F2AF11] shadow-[0_0_0_8px_rgba(242,175,17,0.12)] -translate-x-1/2 z-10" />

                  <div
                    className={`ml-14 md:ml-0 w-full md:w-[44%] bg-white rounded-[28px] p-7 border border-slate-200 shadow-[0_22px_70px_-48px_rgba(8,37,103,0.6)] hover:-translate-y-2 transition-all duration-500 ${
                      isLeft ? "md:mr-auto" : "md:ml-auto"
                    }`}
                  >
                    <div className="flex items-center gap-4 mb-5">
                      <div className="w-14 h-14 rounded-2xl bg-[#082567]/5 text-[#082567] flex items-center justify-center border border-[#082567]/10">
                        <Icon size={25} />
                      </div>

                      <div>
                        <h3
                          className="text-4xl font-black text-[#F2AF11] leading-none"
                          style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
                        >
                          {item.year}
                        </h3>
                      </div>
                    </div>

                    <h4 className="text-xl font-black text-[#082567] mb-3">
                      {item.title}
                    </h4>

                    <p className="text-slate-600 leading-[1.85]">
                      {item.text}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ONNE SECTION */}
      <section className="relative py-28 bg-white overflow-hidden">
        <img
          src={sectionBg}
          alt="Onne Free Zone Background"
          className="absolute inset-0 w-full h-full object-cover opacity-[0.08]"
        />
        <div className="absolute inset-0 bg-white/90" />

        <div className="relative max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-16 items-center">
          <div className="reveal-up">
            <p className="text-[#026B07] font-black tracking-[0.2em] uppercase text-xs mb-4">
              Origin Location
            </p>

            <h2
              className="text-4xl md:text-6xl font-black text-[#082567] leading-tight mb-7"
              style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
            >
              Onne Became the Foundation of Nigeria’s Oil & Gas Free Zone Story
            </h2>

            <p className="text-slate-700 leading-[2] text-[17px] mb-8">
              The Authority began operation in Onne, Rivers State in 2001. The
              Onne Oil and Gas Free Zone became the first dedicated oil and gas
              export free zone and the foundation for Nigeria’s specialized free
              zone regulatory model.
            </p>

            <Link
              to="/free-zones/onne"
              className="inline-flex items-center gap-3 px-8 py-4 rounded-full bg-[#082567] hover:bg-[#F2AF11] hover:text-[#082567] text-white font-black transition-all duration-500"
            >
              Explore Onne Free Zone
              <ArrowUpRight size={20} />
            </Link>
          </div>

          <div className="reveal-up">
            <div className="relative rounded-[38px] bg-white border border-slate-200 p-8 shadow-[0_28px_90px_-55px_rgba(8,37,103,0.55)]">
              <div className="absolute -top-4 -right-4 w-24 h-24 rounded-full border-[18px] border-[#F2AF11]/20" />
              <div className="absolute -bottom-4 -left-4 w-28 h-28 rounded-full border-[18px] border-[#026B07]/15" />

              <div className="relative rounded-[30px] min-h-[390px] bg-[#F8FAFC] flex items-center justify-center overflow-hidden border border-slate-100">
                <div className="absolute w-[76%] h-[76%] rounded-full border border-[#082567]/10" />
                <div className="absolute w-[52%] h-[52%] rounded-full border border-[#026B07]/15" />

                <div className="relative z-10 text-center">
                  <div className="w-24 h-24 mx-auto rounded-[30px] bg-[#082567] text-white flex items-center justify-center mb-6 shadow-xl">
                    <MapPin size={45} />
                  </div>

                  <h3
                    className="text-5xl font-black text-[#082567]"
                    style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
                  >
                    Onne
                  </h3>

                  <p className="text-slate-600 font-bold mt-2">
                    Rivers State, Nigeria
                  </p>
                </div>

                <div className="absolute top-10 right-10 w-6 h-6 rounded-full bg-[#F2AF11]" />
                <div className="absolute bottom-12 left-12 w-5 h-5 rounded-full bg-[#026B07]" />
                <div className="absolute bottom-20 right-20 w-4 h-4 rounded-full bg-[#082567]" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FOUNDATION POINTS */}
      <section className="py-28 bg-[#FAFBFC]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="reveal-up max-w-4xl mx-auto text-center mb-16">
            <p className="text-[#026B07] font-black tracking-[0.2em] uppercase text-xs mb-4">
              Historical Foundation
            </p>

            <h2
              className="text-4xl md:text-6xl font-black text-[#082567] leading-tight"
              style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
            >
              The Institutional Building Blocks
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-7">
            {foundationPoints.map((item, index) => (
              <div
                key={index}
                className="reveal-up group rounded-[30px] p-8 bg-white border border-slate-200 hover:border-[#F2AF11]/70 hover:-translate-y-2 hover:shadow-[0_24px_80px_-55px_rgba(8,37,103,0.6)] transition-all duration-500"
              >
                <div className="w-14 h-14 rounded-2xl bg-[#F2AF11]/15 text-[#082567] flex items-center justify-center mb-7 border border-[#F2AF11]/25">
                  <CheckCircle2 size={26} />
                </div>

                <p className="text-slate-700 leading-[1.9] text-[16px]">
                  {item}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CLOSING */}
      <section className="relative py-24 bg-white overflow-hidden">
        <img
          src={sectionBg}
          alt="OGFZA Legacy Background"
          className="absolute inset-0 w-full h-full object-cover opacity-[0.08]"
        />
        <div className="absolute inset-0 bg-white/92" />

        <div className="relative max-w-7xl mx-auto px-6">
          <div className="reveal-up rounded-[38px] bg-white border border-slate-200 p-8 md:p-12 shadow-[0_26px_95px_-60px_rgba(8,37,103,0.65)]">
            <div className="grid lg:grid-cols-[1fr_auto] gap-10 items-center">
              <div>
                <div className="inline-flex items-center gap-3 text-[#026B07] font-black tracking-[0.2em] uppercase text-xs mb-5">
                  <Layers3 size={18} className="text-[#F2AF11]" />
                  Legacy & Continuity
                </div>

                <h2
                  className="text-3xl md:text-5xl font-black text-[#082567] leading-tight max-w-4xl"
                  style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
                >
                  OGFZA’s history continues through modern regulation, investor
                  confidence and strategic free zone expansion.
                </h2>
              </div>

              <Link
                to="/about/track-record"
                className="inline-flex items-center justify-center gap-3 px-8 py-4 rounded-full bg-[#082567] hover:bg-[#F2AF11] hover:text-[#082567] text-white font-black transition-all duration-500 whitespace-nowrap"
              >
                View Track Record
                <ArrowUpRight size={20} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default OGFZAHistory;